/*
 * Phone sticky "Add to cart" bar for accessory / default product pages.
 * The bar never adds to cart itself: it clicks the real product form button,
 * so variant selection, required options, sold-out/backorder states and the
 * theme's cart behavior all stay in one place. Frame pages have their own bar.
 */
(() => {
  const root = document.documentElement;
  const phone = window.matchMedia('(max-width: 979px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();

  function hasFrameBar() {
    return root.classList.contains('gp-frame-pdp') || root.classList.contains('gp-ninja-pro-pdp') ||
      !!document.querySelector('[data-frame-mobile-sticky-actions]');
  }

  function visibleRect(el) {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? rect : null;
  }

  function init(scope = document) {
    if (hasFrameBar()) return;
    scope.querySelectorAll('[data-gp-acc-sticky]').forEach((bar) => {
      if (controllers.has(bar)) return;
      const section = document.getElementById(`shopify-section-${bar.dataset.sectionId}`) || bar.parentElement;
      const formWrap = section.querySelector('.product__information [data-product-form]') ||
        section.querySelector('[data-product-form]') ||
        section.querySelector('.product__information');
      const button = bar.querySelector('[data-gp-acc-sticky-add]');
      const price = bar.querySelector('[data-gp-acc-sticky-price]');
      const compare = bar.querySelector('[data-gp-acc-sticky-compare]');
      if (!formWrap || !button) return;
      const info = formWrap.closest('.product__information') || section;
      const priceUi = info.querySelector('[data-price-ui]');
      const footer = document.querySelector('footer.footer__container, .shopify-section-group-footer-group, footer');

      let mainButton = null;
      let footerVisible = false;
      let frame = null;
      let pendingOption = null;

      function findMainButton() {
        return formWrap.querySelector('[name="add"], .button--add-to-cart') ||
          info.querySelector('.purchase-details .button--add-to-cart');
      }

      // First option that still needs a choice (variant shows "not-selected").
      function findUnchosenOption() {
        const selection = formWrap.querySelector('variant-selection');
        const selects = [...formWrap.querySelectorAll('options-selection select')];
        const unchosen = selects.find((select) => select.value === 'not-selected' || select.value === '');
        if (unchosen) return unchosen;
        const radios = [...formWrap.querySelectorAll('options-selection fieldset, options-selection [data-variant-option-name]')];
        const openRadio = radios.find((group) => group.matches('fieldset') && !group.querySelector('input:checked'));
        if (openRadio) return openRadio;
        if (selection && selection.getAttribute('variant') === 'not-selected') {
          return formWrap.querySelector('options-selection') || selection;
        }
        return null;
      }

      function optionName(el) {
        if (!el) return '';
        const wrapper = el.closest('.selector-wrapper') || el;
        const label = wrapper.querySelector('[data-variant-option-name]') || wrapper.querySelector('label, legend');
        const name = (label?.getAttribute('data-variant-option-name') || label?.textContent || '').trim();
        return name.length > 0 && name.length <= 16 ? name : '';
      }

      function publishHeight() {
        const height = bar.hidden ? 0 : Math.ceil(bar.getBoundingClientRect().height);
        const value = `${height}px`;
        if (root.style.getPropertyValue('--gp-sticky-cart-height') !== value) {
          root.style.setProperty('--gp-sticky-cart-height', value);
        }
        root.classList.toggle('gp-acc-sticky-visible', height > 0);
      }

      function setText(el, text) {
        if (el && el.textContent !== text) el.textContent = text;
      }

      function sync() {
        frame = null;
        const next = findMainButton();
        if (next !== mainButton) {
          if (mainButton) {
            intersection.unobserve(mainButton);
          }
          buttonChanges.disconnect();
          mainButton = next;
          if (mainButton) {
            intersection.observe(mainButton);
            buttonChanges.observe(mainButton, {
              attributes: true, childList: true, subtree: true, characterData: true,
              attributeFilter: ['disabled', 'class', 'hidden', 'aria-busy', 'aria-disabled'],
            });
          }
        }

        // Show only once the real button has scrolled up out of view.
        const anchor = visibleRect(mainButton) || visibleRect(formWrap);
        const scrolledPast = !!anchor && anchor.bottom < 0;
        const visible = phone.matches && !!mainButton && scrolledPast && !footerVisible;
        if (!visible && !bar.hidden && bar.contains(document.activeElement)) {
          mainButton?.focus({ preventScroll: true });
        }
        if (bar.hidden === visible) bar.hidden = !visible;

        if (mainButton) {
          const mainText = (mainButton.querySelector('.text')?.textContent || mainButton.textContent || '').replace(/\s+/g, ' ').trim();
          const mainDisabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
          const busy = mainButton.getAttribute('aria-busy') === 'true' || /\b(is-loading|loading|is-adding)\b/.test(mainButton.className);
          pendingOption = findUnchosenOption();
          let label;
          let disabled = false;
          if (pendingOption) {
            const name = optionName(pendingOption);
            label = name ? `Select ${name}` : 'Choose options';
          } else if (/notify/i.test(mainText)) {
            label = 'Notify me';
          } else if (/^\s*backorder/i.test(mainText)) {
            label = 'Backorder';
          } else if (mainDisabled || busy) {
            label = mainText && mainText.length <= 18 ? mainText : 'Add to cart';
            disabled = true;
          } else {
            label = mainText && mainText.length <= 18 ? mainText : 'Add to cart';
          }
          setText(button, label);
          if (button.disabled !== disabled) button.disabled = disabled;
          button.toggleAttribute('data-needs-option', !!pendingOption);
        }

        const current = priceUi?.querySelector('.price .money, .price--sale .money, [data-price] .money');
        const previous = priceUi?.querySelector('.compare-at-price .money, [data-compare-at-price] .money');
        if (current) setText(price, current.textContent.trim());
        const previousText = previous?.textContent.trim() || '';
        const showCompare = !!previousText && previous.getClientRects().length > 0 && previousText !== price.textContent;
        if (compare.hidden === showCompare) compare.hidden = !showCompare;
        setText(compare, previousText);
        publishHeight();
      }

      function schedule() {
        if (!frame) frame = requestAnimationFrame(sync);
      }

      const intersection = new IntersectionObserver(schedule, { threshold: [0, 1] });
      intersection.observe(formWrap);
      const footerObserver = footer ? new IntersectionObserver((entries) => {
        footerVisible = entries.some((entry) => entry.isIntersecting);
        schedule();
      }, { threshold: 0 }) : null;
      if (footer) footerObserver.observe(footer);
      const buttonChanges = new MutationObserver(schedule);
      const changes = new MutationObserver(schedule);
      if (priceUi) {
        changes.observe(priceUi, {
          childList: true, subtree: true, characterData: true, attributes: true,
          attributeFilter: ['hidden', 'class', 'style'],
        });
      }
      // Variant changes (selects, swatches, the hidden variant id) and button swaps.
      changes.observe(formWrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['variant', 'disabled', 'data-variant-option-chosen-value'] });
      formWrap.addEventListener('change', schedule);
      const size = new ResizeObserver(schedule);
      size.observe(bar);
      window.addEventListener('resize', schedule, { passive: true });
      phone.addEventListener('change', schedule);

      button.addEventListener('click', () => {
        if (button.disabled || !mainButton) return;
        const option = findUnchosenOption();
        if (option) {
          const target = option.closest('.selector-wrapper') || option;
          target.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          const focusable = option.matches('select, input') ? option : option.querySelector('select, input:not([disabled])');
          focusable?.focus({ preventScroll: true });
          return;
        }
        mainButton.click();
        schedule();
      });

      controllers.set(bar, () => {
        intersection.disconnect();
        footerObserver?.disconnect();
        buttonChanges.disconnect();
        changes.disconnect();
        size.disconnect();
        formWrap.removeEventListener('change', schedule);
        window.removeEventListener('resize', schedule);
        phone.removeEventListener('change', schedule);
        if (frame) cancelAnimationFrame(frame);
        bar.hidden = true;
        publishHeight();
      });
      sync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((destroy, bar) => {
      if (event.target.contains(bar)) {
        destroy();
        controllers.delete(bar);
      }
    });
  });
})();
